import { Box } from "@mui/material";
import Header from "../../../components/Header";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { useRestaurant } from "../../../hooks/RestaurantContext";
import {
  getCouponsError,
  getCouponsLoading,
  getCouponsStatus,
  getCouponsSuccess,
  modifyCoupon,
  updateStatus,
} from "../../../features/couponSlice";
import { toUtcIsoDateTime } from "../../../global/functions/dateFormat";
import CouponForm from "./couponForm";
import { TargetType } from "../../../models/coupon.model";
import BackButton from "../../../components/backButton";
import { useTranslation } from "react-i18next";

const ModifyCoupon = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const data = location.state.coupon;
  const { restaurantId } = useRestaurant();
  const status = useSelector(getCouponsStatus);
  const error = useSelector(getCouponsError);
  const loading = useSelector(getCouponsLoading);
  const success = useSelector(getCouponsSuccess);
  const todayCheck = new Date();
  todayCheck.setHours(0, 0, 0, 0);

  let initialTargetType = TargetType.ALL;
  if (data.categoryType === TargetType.CATEGORIES) initialTargetType = TargetType.CATEGORIES;
  else if (data.categoryType === TargetType.PRODUCTS) initialTargetType = TargetType.PRODUCTS;
  else if (data.categoryType === TargetType.CATEGORY_PRODUCTS)
    initialTargetType = TargetType.CATEGORY_PRODUCTS;

  const initialValues = {
    code: data.code,
    couponType: data.couponType,
    couponValue: data.couponValue,
    minOrderAmount: data.minOrderAmount,
    targetType: initialTargetType,
    couponCategories: data.couponCategories
      ? data.couponCategories.map((cat) => cat._id)
      : [],
    couponProducts: data.couponProducts
      ? data.couponProducts.map((prod) => prod._id)
      : [],
    excludeProducts: data.excludeProducts
      ? data.excludeProducts.map((prod) => prod._id)
      : [],
    isActive: data.isActive,
    startDate: data.startDate ? new Date(data.startDate) : null,
    endDate: data.endDate ? new Date(data.endDate) : null,
    startTime: data.startDate ? data.startDate.substring(11, 16) : "",
    endTime: data.endDate ? data.endDate.substring(11, 16) : "",
    recurringDays: data.recurringDays || [],
    limit: data.limit,
  };

  const handleFormSubmit = (values) => {
    let categoryType = "all";

    let formData = {
      code: values.code,
      couponType: values.couponType,
      couponValue: values.couponValue,
      minOrderAmount: values.minOrderAmount,
      isActive: values.isActive,
      limit: values.limit,
      recurringDays: values.recurringDays || [],
    };

    if (values.startDate && values.startTime) {
      const [startHour, startMinute] = values.startTime.split(":").map(Number);
      formData.startDate = toUtcIsoDateTime(values.startDate, startHour, startMinute);
    }

    if (values.endDate && values.endTime) {
      const [endHour, endMinute] = values.endTime.split(":").map(Number);
      formData.endDate = toUtcIsoDateTime(values.endDate, endHour, endMinute);
    }
    if (values.targetType === TargetType.CATEGORIES) {
      categoryType = "categories";
      formData.categoryType = categoryType;
      formData.couponCategories = values.couponCategories;
    } else if (values.targetType === TargetType.PRODUCTS) {
      categoryType = "products";
      formData.categoryType = categoryType;
      formData.couponProducts = values.couponProducts;
    } else if (values.targetType === TargetType.CATEGORY_PRODUCTS) {
      categoryType = "categories_products";
      formData.categoryType = categoryType;
      formData.couponCategories = values.couponCategories;
      formData.couponProducts = values.couponProducts;
    } else {
      formData.categoryType = "all";
    }
    formData.excludeProducts = values.excludeProducts || [];
    dispatch(
      modifyCoupon({ body: formData, couponId: data._id, restaurantId })
    );
  };

  useEffect(() => {
    if (status === "modifySuccess") {
      toast.success(success);
      navigate("/coupon");
    } else if (status === "modifyError") {
      toast.error(error);
    }
    dispatch(updateStatus());
  }, [status, error, dispatch, navigate, success]);

  return (
    <Box m="20px" className="main-application">
      <BackButton/>
      <Header title={t("headers.editCoupon")} subtitle={t("headers.editCouponSub")} />
      <CouponForm
        buttonText={t("common.edit")}
        handleFormSubmit={handleFormSubmit}
        initialValues={initialValues}
        loading={loading}
      />
    </Box>
  );
};

export default ModifyCoupon;
